<template>
    <div class="ml-5 mr-5 mt-5"  id="waiverSign">
        <v-row>
            <v-col cols="8" class="mx-auto">
                <h1 style="color:black; display: block; text-align: center;">{{ $t("waiver_sign.title") }}</h1>
            </v-col>
        </v-row>
        <v-form ref="formSignWaiver">
            <v-row>
                <v-col sm="5" cols="10" class="mx-auto">
                    <v-select
                        :items="waivers"
                        :label="$t('waiver_sign.waiver')" 
                        v-model="waiver" 
                        outlined dense
                        :hint="$t('waiver_sign.waiver_description')"
                        persistent-hint
                        :rules="[ (v) => !!v || $t('waiver_sign.waiver_required') ]"
                        item-value="idWaiver"
                        item-text="name"
                        return-object
                        :no-data-text="$t('common.no_data')"
                    >
                        <template v-slot:item="{ item, attrs, on }">
                            <v-list-item v-on="on" v-bind="attrs">
                                <v-list-item-content>
                                    <v-list-item-title>
                                          {{item.name}} ({{item.ageTarget}})
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                        <template v-slot:selection="{ item }">
                            {{item.name}} ({{item.ageTarget}})
                        </template>
                    </v-select>
                </v-col>
            </v-row>
            <v-row>
                <v-col sm="5" cols="10" class="mx-auto">
                    <v-select
                        :items="roles"
                        :label="$t('waiver_sign.role')" 
                        v-model="role" 
                        outlined dense
                        :rules="[ (v) => (v || '').length > 0 || $t('waiver_sign.role_required') ]"
                        :no-data-text="$t('common.no_data')"
                    ></v-select>
                </v-col>
            </v-row>
            <v-row>
                <v-col sm="5" cols="10" class="mx-auto">
                    <v-select
                        :items="events"
                        :label="$t('waiver_sign.event')" 
                        v-model="event" 
                        outlined dense
                        :hint="$t('waiver_sign.event_description')"
                        persistent-hint
                        :rules="[ (v) => !!v || $t('waiver_sign.event_required') ]"
                        item-value="fkEventId"
                        item-text="eventName"
                        :no-data-text="$t('common.no_data')"
                    >
                        <template v-slot:selection="{ item }">
                            {{ item.fkEventId }} - {{ item.eventName }} - {{ item.eventDate | formatDateForHumans(false) }}
                        </template>
                        <template v-slot:item="{ item }">
                            {{ item.fkEventId }} - {{ item.eventName }} - {{ item.eventDate | formatDateForHumans(false) }}
                        </template>
                    </v-select>
                </v-col>
            </v-row>
        </v-form>
        <v-row>
            <v-col sm="5" cols="10" class="mx-auto">
                <v-btn color="primary" @click="generateSignWaiver">
                    <v-icon left>mdi-open-in-new</v-icon>
                    {{ $t('waiver_sign.sign_waiver_now') }}
                </v-btn>
            </v-col>
        </v-row>
        <v-form ref="formEmailWaiver">
            <v-row>
                <v-col sm="5" cols="10" class="mx-auto">
                    <v-text-field
                        v-model.trim="email"
                        :label="$t('waiver_sign.email_address')"
                        :hint="$t('waiver_sign.email_helper')"
                        :rules="emailRules"
                        prepend-inner-icon="mdi-email-outline"
                        persistent-hint
                        outlined
                        dense
                    ></v-text-field>
                </v-col>
            </v-row>
        </v-form>
        <v-row>
            <v-col sm="5" cols="10" class="mx-auto">
                <v-btn
                    color="secondary"
                    :loading="sendingEmail"
                    :disabled="sendingEmail"
                    @click="confirmEmailWaiver"
                >
                    <v-icon left>mdi-email-send-outline</v-icon>
                    {{ $t('waiver_sign.send_waiver_email') }}
                </v-btn>
            </v-col>
        </v-row>
        <SGSNotificationConfirm
            v-if="dialogEmailWaiver"
            :notificationMessage="emailWaiverConfirmationMessage"
            @cancel="dialogEmailWaiver = false"
            @agree="sendEmailWaiver"
            type="warning"
        />
        <v-dialog v-model="dialogMinorEmailWaiver" max-width="520">
            <v-card>
                <v-card-title>
                    <span class="headLine">{{ $t("waiver_signed.email_minor_title") }}</span>
                </v-card-title>
                <v-card-text>
                    <p v-if="minorAccountManagers.length === 0">
                        {{ $t("waiver_signed.email_minor_no_account_manager") }}
                    </p>
                    <template v-else>
                        <p>{{ $t("waiver_signed.email_minor_select_account_manager") }}</p>
                        <v-select
                            v-model="selectedAccountManagerId"
                            :items="minorAccountManagers"
                            item-text="name"
                            item-value="id"
                            outlined
                            dense
                            :label="$t('waiver_signed.account_manager')"
                            :no-data-text="$t('common.no_data')"
                        ></v-select>
                        <p v-if="minorEmailWaiverConfirmationMessage">
                            {{ minorEmailWaiverConfirmationMessage }}
                        </p>
                    </template>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="darken-1" dark small text @click="closeMinorEmailWaiverDialog">
                        {{ $t("common.cancel") }}
                    </v-btn>
                    <v-btn
                        v-if="minorAccountManagers.length > 0"
                        color="darken-1"
                        dark
                        small
                        :disabled="!selectedAccountManagerId || sendingEmail"
                        :loading="sendingEmail"
                        @click="sendEmailWaiver"
                    >
                        {{ $t("waiver_sign.send_waiver_email") }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar
            v-model="snack"
            :color="color_snack"
            :timeout="5000"
            top
            right
        >
            {{ msg_snack }}
            <template v-slot:action="{ attrs }">
                <v-btn text v-bind="attrs" @click="snack = false">
                    {{ $t("common.close") }}
                </v-btn>
            </template>
        </v-snackbar>
    </div>
</template>

<script>
import { apiCalls } from "@/mixins/apiCalls.js";
import SGSNotificationConfirm from "@/components/custom/notification/NotificationConfirm.vue";

export default {
    components: { SGSNotificationConfirm },
    mixins: [apiCalls],
    data() {
        return {
            waivers: [],
            waiver: null,
            event: null,
            role: null,
            email: "",
            dialogEmailWaiver: false,
            dialogMinorEmailWaiver: false,
            minorAccountManagers: [],
            selectedAccountManagerId: null,
            sendingEmail: false,
            snack: false,
            color_snack: "success",
            msg_snack: ""
        };
    },
    computed: {
        emailRules() {
            return [
                (v) => !!v || this.$t("waiver_sign.email_required"),
                (v) => /.+@.+\..+/.test(v || "") || this.$t("waiver_sign.email_invalid")
            ];
        },
        roles() {
            if(this.waiver) {
                return this.waiver.waiverTypeArray.map((el) => {
                    return {
                        value: el,
                        text: this.$t(`common.${el.toLowerCase()}`)
                    };
                });
            }
            return [];
        },
        events() {
            if(this.role && this.waiver) {
                let events = this.waiver.eventWaivers;
                events = events.filter((ele) => {
                    return ele.waiverTypeArray.includes(this.role);
                }).map((el) => {
                    return { ... el, eventName: this.$escapeHtml.decode(el.eventName) };
                });
                return events;
            }
            return [];
        },
        selectedEventWaiver() {
            if(!this.event) return null;
            return this.events.find((eventWaiver) => {
                return String(eventWaiver.fkEventId) === String(this.event);
            }) || null;
        },
        emailWaiverConfirmationMessage() {
            return this.$t("waiver_sign.email_confirmation_message", {
                waiver: this.waiver ? this.waiver.name : "",
                event: this.selectedEventWaiver ? this.selectedEventWaiver.eventName : "",
                email: this.email
            });
        },
        minorEmailWaiverConfirmationMessage() {
            if(!this.selectedAccountManagerId) return "";
            const manager = this.minorAccountManagers.find((accountManager) => {
                return String(accountManager.id) === String(this.selectedAccountManagerId);
            });
            if(!manager) return "";

            return this.$t("waiver_signed.email_confirmation_message", {
                name: `${manager.firstName || ""} ${manager.lastName || ""}`.trim(),
                email: manager.emailAddress || ""
            });
        }
    },
    methods: {
        async getDataWaiver() {
            const response = await this.getApi('/api/v1/promoter/waivers/getDataForSign');
            
            if(response.success) {
                this.waivers = response.message.waivers;
            }
        },
        generateSignWaiver() {
            if(this.$refs.formSignWaiver.validate()) {
                let data = {
                    waiver: this.waiver,
                    role: this.role,
                    event: this.event
                };
                let _item = JSON.stringify(data);
                localStorage.setItem('waiver_promoter', _item);
                const routeData = this.$router.resolve({name: 'WaiverSignForm'});
                window.open(routeData.href, '_blank');
                //this.$router.push({ name: 'WaiverSignForm' });
            }
        },
        confirmEmailWaiver() {
            const waiverSelectionValid = this.$refs.formSignWaiver.validate();
            const emailValid = this.$refs.formEmailWaiver.validate();

            if(!waiverSelectionValid || !emailValid) return;
            if(!this.selectedEventWaiver || !this.selectedEventWaiver.id) {
                this.showSnackBar(this.$t("waiver_sign.not_found_waiverId"), "error");
                return;
            }

            this.dialogEmailWaiver = true;
        },
        async sendEmailWaiver() {
            this.dialogEmailWaiver = false;
            this.dialogMinorEmailWaiver = false;
            this.sendingEmail = true;

            try {
                const payload = {
                    eventId: this.event,
                    eventWaiverId: this.selectedEventWaiver.id,
                    role: this.role,
                    email: this.email
                };
                if(this.selectedAccountManagerId) {
                    payload.accountManagerUserId = this.selectedAccountManagerId;
                }

                const response = await this.api("post", "/api/v1/promoter/waivers/email-by-address", payload);

                if(response && response.success === 1) {
                    this.showSnackBar(this.$t("waiver_sign.email_sent"));
                    this.closeMinorEmailWaiverDialog();
                    return;
                }

                if(this.requiresAccountManager(response)) {
                    this.openMinorEmailWaiverDialog(response.message);
                    return;
                }

                this.closeMinorEmailWaiverDialog();
                this.showSnackBar(this.emailErrorMessage(response), "error");
            } catch (error) {
                this.closeMinorEmailWaiverDialog();
                this.showSnackBar(this.$t("waiver_sign.waiver_email_failed"), "error");
            } finally {
                this.sendingEmail = false;
            }
        },
        emailErrorMessage(response) {
            let message = response && response.message ? response.message : null;
            if(Array.isArray(message)) {
                message = message[0];
            } else if(message && message.error) {
                message = message.error;
            }

            return this.translatedEmailMessage(message || "waiver_email_failed");
        },
        requiresAccountManager(response) {
            return response &&
                response.message &&
                response.message.error === "minor_account_manager_required";
        },
        openMinorEmailWaiverDialog(message) {
            this.minorAccountManagers = Array.isArray(message.accountManagers) ? message.accountManagers : [];
            this.selectedAccountManagerId = this.minorAccountManagers.length === 1 ? this.minorAccountManagers[0].id : null;
            this.dialogMinorEmailWaiver = true;
        },
        closeMinorEmailWaiverDialog() {
            this.dialogMinorEmailWaiver = false;
            this.minorAccountManagers = [];
            this.selectedAccountManagerId = null;
        },
        translatedEmailMessage(message) {
            const waiverSignKey = `waiver_sign.${message}`;
            if(this.$te && this.$te(waiverSignKey)) {
                return this.$t(waiverSignKey);
            }

            const waiverSignedKey = `waiver_signed.${message}`;
            if(this.$te && this.$te(waiverSignedKey)) {
                return this.$t(waiverSignedKey);
            }

            return this.$t("waiver_sign.waiver_email_failed");
        },
        showSnackBar(message, color = "success") {
            this.msg_snack = message;
            this.color_snack = color;
            this.snack = true;
        }
    },
    mounted() {
        this.getDataWaiver();
    },
    watch: {
        events(val) {
            if(val.length > 0) {
                this.event = this.events[0].fkEventId;
            } else {
                this.event = null;
            }
        }
    }
}
</script>
